/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { setTitle } from "@lemmonhq/orders";
import { useToast } from "@app/vue/providers/Toast";
import { useAuth, useShop, useTrpc } from "@app/vue/providers/index";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import {
  useLocalization,
  RightSheet,
  Loading,
  ErrorRenderer,
  ResponsiveTable,
} from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import type { DiscountType, DiscountFormState } from "@lemmonhq/types";
import { formatDiscountValue } from "./utils";
import { DiscountTypeForm } from "./DiscountTypeForm";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";

export const DiscountTypesSettings = defineComponent({
  name: "DiscountTypesSettings",
  setup() {
    const toast = useToast();
    const { t } = useLocalization();
    const trpc = useTrpc();
    const queryClient = useQueryClient();
    const auth = useAuth();
    const isSheetOpen = ref(false);
    const selectedDiscount = ref<DiscountType | null>(null);
    const mode = ref<"create" | "edit">("create");

    const {
      data: discountTypes,
      isPending,
      isError,
      error,
      refetch,
    } = useQuery({
      queryKey: ["discountTypes", "list"],
      queryFn: () => trpc.discountTypes.list.query(), //as Promise<DiscountType[]>
    });

    const createMutation = useMutation({
      mutationFn: (input: {
        title: string;
        default: { type: "fixed-amount" | "percentage"; value: number };
        allow_changes: boolean;
      }) => trpc.discountTypes.create.mutate(input),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["discountTypes"] });
        toast.showToast("Discount type created successfully.", {
          type: "success",
        });
      },
      onError: (error) => {
        console.error("Failed to create discount type", error);
        toast.showToast("Failed to create discount type. Please try again.", {
          type: "error",
        });
      },
    });

    const updateMutation = useMutation({
      mutationFn: (input: {
        id: string;
        data: {
          title: string;
          default: { type: "fixed-amount" | "percentage"; value: number };
          allow_changes: boolean;
        };
      }) => trpc.discountTypes.update.mutate(input),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["discountTypes"] });
        toast.showToast("Discount type updated successfully.", {
          type: "success",
        });
      },
      onError: (error) => {
        console.error("Failed to update discount type", error);
        toast.showToast("Failed to update discount type. Please try again.", {
          type: "error",
        });
      },
    });

    const deleteMutation = useMutation({
      mutationFn: (id: string) => trpc.discountTypes.delete.mutate({ id }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["discountTypes"] });
        toast.showToast("Discount type deleted successfully.", {
          type: "success",
        });
      },
      onError: (error) => {
        console.error("Failed to delete discount type", error);
        toast.showToast("Failed to delete discount type. Please try again.", {
          type: "error",
        });
      },
    });

    const openCreateSheet = () => {
      mode.value = "create";
      selectedDiscount.value = null;
      isSheetOpen.value = true;
    };

    const openEditSheet = (discount: DiscountType) => {
      mode.value = "edit";
      selectedDiscount.value = discount;
      isSheetOpen.value = true;
    };

    const closeSheet = () => {
      isSheetOpen.value = false;
      selectedDiscount.value = null;
    };

    const handleCreate = async (formData: DiscountFormState) => {
      const value = parseFloat(formData.value);
      await createMutation.mutateAsync({
        title: formData.title.trim(),
        default: {
          type: formData.type,
          value,
        },
        allow_changes: formData.allow_changes,
      });
      closeSheet();
    };

    const handleUpdate = async (id: string, formData: DiscountFormState) => {
      const value = parseFloat(formData.value);
      await updateMutation.mutateAsync({
        id,
        data: {
          title: formData.title.trim(),
          default: {
            type: formData.type,
            value,
          },
          allow_changes: formData.allow_changes,
        },
      });
      closeSheet();
    };

    const handleDelete = async (id: string) => {
      await deleteMutation.mutateAsync(id);
      closeSheet();
    };

    onMounted(() => {
      setTitle("Discount Types");
    });

    const columns: ResponsiveTableColumn[] = [
      {
        key: "d-name",
        label: "Name",
        icon: "local_offer",
        mobilePriority: "hidden",
        render: (discount: DiscountType) => (
          <div class="font-medium text-base-content">{discount.name}</div>
        ),
      },
      {
        key: "d-type",
        label: "Type",
        icon: "sell",
        mobilePriority: "hidden",
        render: (discount: DiscountType) =>
          discount.default.type === "percentage"
            ? t("settings.discount_types_page.type_percentage")
            : t("settings.discount_types_page.type_fixed_amount"),
      },
      {
        key: "d-value",
        label: "Value",
        icon: "percent",
        mobilePriority: "hidden",
        render: (discount: DiscountType) => (
          <span class="font-medium text-base-content">
            {formatDiscountValue(
              discount.default.type,
              discount.default.value,
              auth.user.value?.restaurant.currency || "CHF",
            )}
          </span>
        ),
      },
      {
        key: "d-adjustment",
        label: "Adjustment",
        icon: "tune",
        mobilePriority: "hidden",
        render: (discount: DiscountType) => (
          <span
            class={`badge badge-sm ${discount.allow_changes ? "badge-success" : "badge-neutral"}`}
          >
            {discount.allow_changes
              ? t("settings.discount_types_page.badge_flexible")
              : t("settings.discount_types_page.badge_fixed")}
          </span>
        ),
      },
      {
        key: "m-name",
        label: "Name",
        desktopHide: true,
        mobilePriority: "primary",
        render: (discount: DiscountType) => (
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-base-content">
              {discount.name}
            </div>
            <div class="mt-0.5 flex items-center gap-1 text-xs text-base-content/60">
              <span class="material-symbols-outlined text-sm">
                {discount.allow_changes ? "edit" : "lock"}
              </span>
              <span class="truncate">
                {discount.allow_changes
                  ? t("settings.discount_types_page.staff_can_adjust")
                  : t("settings.discount_types_page.value_locked")}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: "m-adjustment",
        label: "Adjustment",
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center shrink-0",
        render: (discount: DiscountType) => (
          <span
            class={`badge badge-sm ${discount.allow_changes ? "badge-success" : "badge-neutral"}`}
          >
            {discount.allow_changes
              ? t("settings.discount_types_page.badge_flexible")
              : t("settings.discount_types_page.badge_fixed")}
          </span>
        ),
      },
      {
        key: "m-type",
        label: "Type",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (discount: DiscountType) =>
          discount.default.type === "percentage"
            ? t("settings.discount_types_page.type_percentage")
            : t("settings.discount_types_page.type_fixed_amount"),
      },
      {
        key: "m-value",
        label: "Value",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (discount: DiscountType) =>
          formatDiscountValue(
            discount.default.type,
            discount.default.value,
            auth.user.value?.restaurant.currency || "CHF",
          ),
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (discount: DiscountType) => (
          <button
            class="btn btn-sm btn-secondary"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              openEditSheet(discount);
            }}
          >
            <span class="material-symbols-outlined text-2xl">table_edit</span>
          </button>
        ),
      },
    ];

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="local_offer"
          title={t("settings.discount_types_page.page_title")}
          description={t("settings.discount_types_page.page_description")}
        >
          {{
            actions: () => (
              <button
                disabled={isPending.value}
                class="btn btn-primary btn-sm gap-2 shadow-lg"
                onClick={openCreateSheet}
              >
                <span class="material-symbols-outlined text-base">add</span>
                {t("settings.discount_types_page.new_discount_button")}
              </button>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding>
            {isPending.value && (
              <div class="flex h-80 items-center justify-center">
                <Loading />
              </div>
            )}

            {!isPending.value && isError.value && (
              <ErrorRenderer
                error={error.value instanceof Error ? error.value : null}
                message="Failed to load discount types."
                onRetry={() => void refetch()}
              />
            )}

            {!isPending.value &&
              !isError.value &&
              (!discountTypes.value || discountTypes.value.length === 0) && (
                <div class="flex min-h-[320px] flex-col items-center justify-center px-8 py-16 text-center">
                  <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-200">
                    <span class="material-symbols-outlined text-3xl text-base-content/40">
                      local_offer
                    </span>
                  </div>
                  <p class="mb-2 font-medium text-base-content">
                    {t("settings.discount_types_page.empty_title")}
                  </p>
                  <p class="text-sm text-base-content/60">
                    {t("settings.discount_types_page.empty_description")}
                  </p>
                </div>
              )}

            {!isPending.value &&
              !isError.value &&
              discountTypes.value &&
              discountTypes.value.length > 0 && (
                <ResponsiveTable
                  columns={columns}
                  rows={discountTypes.value}
                  rowKey={(discount: DiscountType) => discount.id}
                  onRowClick={openEditSheet}
                  emptyMessage={t("settings.discount_types_page.empty_title")}
                />
              )}
          </AppSubpageContent>
        </AppSubpage>
        {isSheetOpen.value ? (
          <RightSheet
            isOpen={isSheetOpen.value}
            title={
              mode.value === "create"
                ? t("settings.discount_types_page.form_create_title")
                : t("settings.discount_types_page.form_edit_title")
            }
            onClose={closeSheet}
            width="560px"
          >
            <DiscountTypeForm
              mode={mode.value}
              discount={selectedDiscount.value}
              onSubmit={
                mode.value === "create"
                  ? handleCreate
                  : (formData) =>
                      handleUpdate(selectedDiscount.value!.id, formData)
              }
              onDelete={
                mode.value === "edit" && selectedDiscount.value
                  ? () => handleDelete(selectedDiscount.value!.id)
                  : undefined
              }
              onCancel={closeSheet}
            />
          </RightSheet>
        ) : null}
      </div>
    );
  },
});
